/**
 * Advanced Complexity Analyzer v3.0
 * 
 * Super accurate complexity detection using:
 * - Multi-factor pattern analysis
 * - Domain-specific detection (accounting, law, etc.)
 * - Computational requirement analysis
 * - Question structure analysis
 * - Context awareness
 * - Response length prediction
 * - Technical depth scoring
 */

import { Attachment } from '../types';

export type ComplexityLevel = 'simple' | 'moderate' | 'complex' | 'veryComplex';

export interface ComplexityAnalysis {
    level: ComplexityLevel;
    score: number;
    confidence: number; // 0-1
    factors: ComplexityFactor[];
    reasoning: string;
    estimatedResponseLength: number; // in characters
    requiresComputation: boolean;
    requiresMultiStep: boolean;
    domainSpecialty?: string;
}

export interface ComplexityFactor {
    name: string;
    impact: number; // -3 to +3
    weight: number; // 0-1
    detected: boolean;
    examples?: string[];
}

class AdvancedComplexityAnalyzer {
    
    /**
     * Analyze message complexity with high accuracy
     */
    analyze(
        message: string,
        attachments: Attachment[] = [],
        conversationHistory: number = 0
    ): ComplexityAnalysis {
        
        const factors: ComplexityFactor[] = [];
        let totalScore = 0;
        let totalWeight = 0;
        
        const lower = message.toLowerCase();
        const wordCount = message.split(/\s+/).filter(w => w.length > 0).length;
        const sentenceCount = message.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
        const avgWordsPerSentence = sentenceCount > 0 ? wordCount / sentenceCount : wordCount;
        
        // ==================== FACTOR 1: MESSAGE LENGTH & STRUCTURE ====================
        const lengthFactor = this.analyzeLengthStructure(wordCount, sentenceCount, avgWordsPerSentence);
        factors.push(lengthFactor);
        if (lengthFactor.detected) {
            totalScore += lengthFactor.impact * lengthFactor.weight;
            totalWeight += lengthFactor.weight;
        }
        
        // ==================== FACTOR 2: QUESTION TYPE ====================
        const questionFactor = this.analyzeQuestionType(message, lower);
        factors.push(questionFactor);
        if (questionFactor.detected) {
            totalScore += questionFactor.impact * questionFactor.weight;
            totalWeight += questionFactor.weight;
        }
        
        // ==================== FACTOR 3: COMPUTATIONAL REQUIREMENTS ====================
        const computationFactor = this.analyzeComputationalRequirements(message, lower);
        factors.push(computationFactor);
        if (computationFactor.detected) {
            totalScore += computationFactor.impact * computationFactor.weight;
            totalWeight += computationFactor.weight;
        }
        
        // ==================== FACTOR 4: DOMAIN SPECIALTY ====================
        const domainFactor = this.analyzeDomainSpecialty(lower);
        factors.push(domainFactor);
        let domainSpecialty: string | undefined;
        if (domainFactor.detected) {
            totalScore += domainFactor.impact * domainFactor.weight;
            totalWeight += domainFactor.weight;
            domainSpecialty = domainFactor.name;
        }
        
        // ==================== FACTOR 5: MULTI-STEP ANALYSIS ====================
        const multiStepFactor = this.analyzeMultiStep(message, lower);
        factors.push(multiStepFactor);
        if (multiStepFactor.detected) {
            totalScore += multiStepFactor.impact * multiStepFactor.weight;
            totalWeight += multiStepFactor.weight;
        }
        
        // ==================== FACTOR 6: TECHNICAL DEPTH ====================
        const depthFactor = this.analyzeTechnicalDepth(message, lower);
        factors.push(depthFactor);
        if (depthFactor.detected) {
            totalScore += depthFactor.impact * depthFactor.weight;
            totalWeight += depthFactor.weight;
        }
        
        // ==================== FACTOR 7: OUTPUT REQUIREMENTS ====================
        const outputFactor = this.analyzeOutputRequirements(lower);
        factors.push(outputFactor);
        if (outputFactor.detected) {
            totalScore += outputFactor.impact * outputFactor.weight;
            totalWeight += outputFactor.weight;
        }
        
        // ==================== FACTOR 8: FILE ATTACHMENTS ====================
        if (attachments.length > 0) {
            const attachmentFactor = this.analyzeAttachments(attachments);
            factors.push(attachmentFactor);
            totalScore += attachmentFactor.impact * attachmentFactor.weight;
            totalWeight += attachmentFactor.weight;
        }
        
        // ==================== FACTOR 9: CONVERSATION CONTEXT ====================
        if (conversationHistory > 0) {
            const contextFactor = this.analyzeContext(conversationHistory);
            factors.push(contextFactor);
            totalScore += contextFactor.impact * contextFactor.weight;
            totalWeight += contextFactor.weight;
        }
        
        // ==================== CALCULATE FINAL SCORE ====================
        const normalizedScore = totalWeight > 0 ? totalScore / totalWeight : 0;
        const confidence = Math.min(1, totalWeight / 5); // Max confidence when 5+ factors detected
        
        // Map score to complexity level with hysteresis
        const level = this.scoreToLevel(normalizedScore);
        
        // Estimate response length based on complexity and question type
        const estimatedResponseLength = this.estimateResponseLength(
            level,
            questionFactor.detected,
            computationFactor.detected,
            multiStepFactor.detected
        );
        
        // Generate reasoning
        const reasoning = this.generateReasoning(factors.filter(f => f.detected), level);
        
        return {
            level,
            score: normalizedScore,
            confidence,
            factors,
            reasoning,
            estimatedResponseLength,
            requiresComputation: computationFactor.detected,
            requiresMultiStep: multiStepFactor.detected,
            domainSpecialty
        };
    }
    
    // ==================== FACTOR ANALYZERS ====================
    
    private analyzeLengthStructure(
        wordCount: number,
        sentenceCount: number,
        avgWordsPerSentence: number
    ): ComplexityFactor {
        let impact = 0;
        let detected = false;
        
        // Very short = simple
        if (wordCount < 5) {
            impact = -2;
            detected = true;
        }
        // Short = likely simple/moderate
        else if (wordCount < 15) {
            impact = -1;
            detected = true;
        }
        // Medium = moderate
        else if (wordCount < 40) {
            impact = 0;
            detected = true;
        }
        // Long = complex
        else if (wordCount < 80) {
            impact = 1;
            detected = true;
        }
        // Very long = very complex
        else {
            impact = 2;
            detected = true;
        }
        
        // Multiple sentences increase complexity
        if (sentenceCount > 3) {
            impact += 0.5;
        }
        
        // Long complex sentences increase complexity
        if (avgWordsPerSentence > 20) {
            impact += 0.5;
        }
        
        return {
            name: 'Message Length & Structure',
            impact: Math.min(3, Math.max(-3, impact)),
            weight: 0.8,
            detected,
            examples: [`${wordCount} words, ${sentenceCount} sentences`]
        };
    }
    
    private analyzeQuestionType(message: string, lower: string): ComplexityFactor {
        const patterns = {
            simple: [
                /^(what is|who is|when|where|is it|true or false|yes or no)\b/i,
                /^(hi|hello|hey|thanks|thank you|ok|okay)\b/i,
                /^define\b/i,
                /\b(meaning of)\b/i
            ],
            moderate: [
                /^(how|why|explain|describe|tell me)\b/i,
                /\b(what are|list|give me|show me)\b/i,
                /\b(difference between|compare)\b/i,
                /\b(example|illustrate)\b/i
            ],
            complex: [
                /\b(analyze|evaluate|assess|determine)\b/i,
                /\b(calculate|compute|solve|find)\b/i,
                /\b(how to|procedure|process|steps)\b/i,
                /\b(advantages and disadvantages|pros and cons)\b/i,
                /\b(thoroughly|in detail|comprehensively)\b/i
            ],
            veryComplex: [
                /\b(critically analyze|critically evaluate)\b/i,
                /\b(comprehensive analysis|full discussion)\b/i,
                /\b(all aspects of|everything about)\b/i,
                /\b(synthesize|integrate|consolidate)\b/i,
                /\b(advanced|expert|professional level)\b/i
            ]
        };
        
        let impact = 0;
        let detected = false;
        const matches: string[] = [];
        
        if (patterns.simple.some(p => p.test(message))) {
            impact = -2;
            detected = true;
            matches.push('Simple question type');
        }
        else if (patterns.veryComplex.some(p => p.test(message))) {
            impact = 3;
            detected = true;
            matches.push('Very complex question type');
        }
        else if (patterns.complex.some(p => p.test(message))) {
            impact = 2;
            detected = true;
            matches.push('Complex question type');
        }
        else if (patterns.moderate.some(p => p.test(message))) {
            impact = 0.5;
            detected = true;
            matches.push('Moderate question type');
        }
        
        return {
            name: 'Question Type',
            impact,
            weight: 1.0,
            detected,
            examples: matches
        };
    }
    
    private analyzeComputationalRequirements(message: string, lower: string): ComplexityFactor {
        const patterns = {
            basic: [
                /\b(add|subtract|multiply|divide|sum|total)\b/i,
                /\b(\d+\s*[+\-×÷]\s*\d+)/
            ],
            moderate: [
                /\b(percentage|percent|ratio|proportion)\b/i,
                /\b(average|mean|median|mode)\b/i,
                /\b(formula|equation)\b/i
            ],
            advanced: [
                /\b(calculate|compute|determine|solve|find)\b/i,
                /\b(depreciation|amortization|present value|future value)\b/i,
                /\b(interest|compound|annuity|discount)\b/i,
                /\b(variance|standard deviation|correlation)\b/i,
                /\b(regression|statistical|probability)\b/i,
                /\b(optimization|minimiz|maximiz)\b/i
            ],
            expert: [
                /\b(differential|integral|derivative)\b/i,
                /\b(matrix|determinant|eigenvalue)\b/i,
                /\b(consolidat.*calculation|complex.*calculation)\b/i,
                /\b(deferred.*tax|effective.*tax.*rate)\b/i
            ]
        };
        
        let impact = 0;
        let detected = false;
        const matches: string[] = [];
        
        // Count numbers in message (indicates calculations)
        const numberCount = (message.match(/\b\d+(\.\d+)?\b/g) || []).length;
        
        if (patterns.expert.some(p => p.test(message))) {
            impact = 3;
            detected = true;
            matches.push('Expert-level computation');
        }
        else if (patterns.advanced.some(p => p.test(message))) {
            impact = 2;
            detected = true;
            matches.push('Advanced computation required');
        }
        else if (patterns.moderate.some(p => p.test(message))) {
            impact = 1;
            detected = true;
            matches.push('Moderate computation');
        }
        else if (patterns.basic.some(p => p.test(message))) {
            impact = 0.5;
            detected = true;
            matches.push('Basic computation');
        }
        
        // Multiple numbers = more complex calculation
        if (numberCount > 3) {
            impact += 0.5;
            matches.push(`${numberCount} numbers detected`);
            detected = true;
        }
        
        return {
            name: 'Computational Requirements',
            impact: Math.min(3, impact),
            weight: 1.0,
            detected,
            examples: matches
        };
    }
    
    private analyzeDomainSpecialty(lower: string): ComplexityFactor {
        const domains = {
            'Accounting Standards (PFRS/IFRS)': {
                patterns: [
                    /\b(pfrs|pas|ifrs|ias|gaap)\s*\d+/i,
                    /\b(accounting standard|financial reporting standard)\b/i,
                    /\b(revenue recognition|lease accounting|business combination)\b/i,
                    /\b(impairment|consolidation|fair value)\b/i
                ],
                impact: 2.5
            },
            'Taxation': {
                patterns: [
                    /\b(income tax|value added tax|vat|withholding tax|bir)\b/i,
                    /\b(tax return|tax computation|taxable income)\b/i,
                    /\b(deferred tax|tax planning|tax compliance)\b/i
                ],
                impact: 2
            },
            'Audit & Assurance': {
                patterns: [
                    /\b(audit|auditor|internal control|risk assessment)\b/i,
                    /\b(audit procedure|audit evidence|materiality)\b/i,
                    /\b(substantive test|test of control)\b/i
                ],
                impact: 2
            },
            'Financial Analysis': {
                patterns: [
                    /\b(financial statement analysis|ratio analysis)\b/i,
                    /\b(liquidity ratio|profitability ratio|leverage ratio)\b/i,
                    /\b(trend analysis|horizontal analysis|vertical analysis)\b/i
                ],
                impact: 1.5
            },
            'Cost Accounting': {
                patterns: [
                    /\b(cost accounting|variance analysis|budgeting)\b/i,
                    /\b(job order|process costing|activity based costing)\b/i,
                    /\b(standard cost|overhead allocation)\b/i
                ],
                impact: 1.5
            },
            'Legal/Law': {
                patterns: [
                    /\b(legal|law|statute|regulation|jurisdiction)\b/i,
                    /\b(contract|agreement|liability|tort)\b/i,
                    /\b(jurisprudence|case law|precedent)\b/i,
                    /\b(litigation|arbitration|dispute)\b/i
                ],
                impact: 2
            }
        };
        
        for (const [domain, config] of Object.entries(domains)) {
            if (config.patterns.some(p => p.test(lower))) {
                return {
                    name: domain,
                    impact: config.impact,
                    weight: 0.9,
                    detected: true,
                    examples: [domain]
                };
            }
        }
        
        return {
            name: 'Domain Specialty',
            impact: 0,
            weight: 0.9,
            detected: false
        };
    }
    
    private analyzeMultiStep(message: string, lower: string): ComplexityFactor {
        const indicators = [
            /\b(step|steps|first|second|third|then|next|finally)\b/i,
            /\b(multiple|several|various|different)\b/i,
            /\band\b.*\band\b/i, // Multiple 'and' suggests multiple parts
            /[;,].*[;,]/, // Multiple clauses
            /\b(both|all|each|every)\b/i,
            /\b(as well as|in addition|furthermore|moreover)\b/i,
            /\d+\)|\d+\./, // Numbered lists
            /\b(requirement|requirements|criteria)\b/i
        ];
        
        const matchCount = indicators.filter(p => p.test(message)).length;
        
        // Count question marks (multiple questions)
        const questionCount = (message.match(/\?/g) || []).length;
        
        let impact = 0;
        let detected = false;
        const matches: string[] = [];
        
        if (matchCount >= 4 || questionCount >= 3) {
            impact = 2.5;
            detected = true;
            matches.push('Highly multi-step problem');
        }
        else if (matchCount >= 2 || questionCount >= 2) {
            impact = 1.5;
            detected = true;
            matches.push('Multi-step problem');
        }
        else if (matchCount >= 1) {
            impact = 0.5;
            detected = true;
            matches.push('Some multi-step elements');
        }
        
        return {
            name: 'Multi-Step Analysis',
            impact,
            weight: 0.9,
            detected,
            examples: matches
        };
    }
    
    private analyzeTechnicalDepth(message: string, lower: string): ComplexityFactor {
        const depthIndicators = {
            shallow: [
                /\b(brief|quick|simple|basic)\b/i,
                /\b(short answer|one sentence)\b/i
            ],
            moderate: [
                /\b(explain|describe|summary|overview)\b/i,
                /\b(example|illustrate)\b/i
            ],
            deep: [
                /\b(detailed|thorough|in-depth|comprehensive)\b/i,
                /\b(analyze|evaluate|critically)\b/i,
                /\b(all aspects|complete|full)\b/i,
                /\b(theoretical|conceptual|framework)\b/i
            ],
            expert: [
                /\b(expert|professional|advanced|sophisticated)\b/i,
                /\b(research|academic|scholarly)\b/i,
                /\b(technical specification|implementation)\b/i
            ]
        };
        
        let impact = 0;
        let detected = false;
        const matches: string[] = [];
        
        if (depthIndicators.expert.some(p => p.test(message))) {
            impact = 3;
            detected = true;
            matches.push('Expert-level depth required');
        }
        else if (depthIndicators.deep.some(p => p.test(message))) {
            impact = 2;
            detected = true;
            matches.push('Deep analysis required');
        }
        else if (depthIndicators.moderate.some(p => p.test(message))) {
            impact = 0.5;
            detected = true;
            matches.push('Moderate depth');
        }
        else if (depthIndicators.shallow.some(p => p.test(message))) {
            impact = -1;
            detected = true;
            matches.push('Shallow depth requested');
        }
        
        return {
            name: 'Technical Depth',
            impact,
            weight: 0.8,
            detected,
            examples: matches
        };
    }
    
    private analyzeOutputRequirements(lower: string): ComplexityFactor {
        const outputPatterns = {
            simple: [
                /\b(yes|no|true|false|one word)\b/i
            ],
            structured: [
                /\b(table|chart|list|enumerate|bullet points)\b/i,
                /\b(format|organize|structure)\b/i,
                /\b(diagram|visual|graph)\b/i
            ],
            complex: [
                /\b(report|documentation|essay|paper)\b/i,
                /\b(code|program|script|algorithm)\b/i,
                /\b(journal entry|financial statement)\b/i,
                /\b(solution with explanation|show your work)\b/i
            ]
        };
        
        let impact = 0;
        let detected = false;
        const matches: string[] = [];
        
        if (outputPatterns.complex.some(p => p.test(lower))) {
            impact = 2;
            detected = true;
            matches.push('Complex output format required');
        }
        else if (outputPatterns.structured.some(p => p.test(lower))) {
            impact = 1;
            detected = true;
            matches.push('Structured output required');
        }
        else if (outputPatterns.simple.some(p => p.test(lower))) {
            impact = -1.5;
            detected = true;
            matches.push('Simple output format');
        }
        
        return {
            name: 'Output Requirements',
            impact,
            weight: 0.7,
            detected,
            examples: matches
        };
    }
    
    private analyzeAttachments(attachments: Attachment[]): ComplexityFactor {
        let impact = 0;
        const count = attachments.length;
        const types = new Set(attachments.map(a => a.mimeType.split('/')[0]));
        
        // Base complexity for attachments
        impact += Math.min(2, count * 0.5);
        
        // Different types increase complexity
        if (types.size > 1) {
            impact += 0.5;
        }
        
        // PDFs are complex
        if (attachments.some(a => a.mimeType.includes('pdf'))) {
            impact += 0.5;
        }
        
        return {
            name: 'File Attachments',
            impact: Math.min(3, impact),
            weight: 0.8,
            detected: true,
            examples: [`${count} file(s) attached`]
        };
    }
    
    private analyzeContext(conversationHistory: number): ComplexityFactor {
        let impact = 0;
        
        // Longer conversations might be follow-ups (potentially simpler)
        // or building on complex topics (potentially more complex)
        if (conversationHistory > 10) {
            impact = 0.5; // Moderate increase for extensive context
        } else if (conversationHistory > 5) {
            impact = 0.3;
        }
        
        return {
            name: 'Conversation Context',
            impact,
            weight: 0.4,
            detected: conversationHistory > 0,
            examples: [`${conversationHistory} previous messages`]
        };
    }
    
    // ==================== SCORING & MAPPING ====================
    
    private scoreToLevel(score: number): ComplexityLevel {
        // Thresholds with hysteresis to prevent flickering
        if (score < -0.5) return 'simple';
        if (score < 0.8) return 'moderate';
        if (score < 1.8) return 'complex';
        return 'veryComplex';
    }
    
    private estimateResponseLength(
        level: ComplexityLevel,
        hasQuestion: boolean,
        hasComputation: boolean,
        isMultiStep: boolean
    ): number {
        let baseLength = {
            simple: 150,
            moderate: 400,
            complex: 800,
            veryComplex: 1500
        }[level];
        
        if (hasComputation) baseLength *= 1.3;
        if (isMultiStep) baseLength *= 1.4;
        if (!hasQuestion) baseLength *= 0.8;
        
        return Math.floor(baseLength);
    }
    
    private generateReasoning(factors: ComplexityFactor[], level: ComplexityLevel): string {
        const positiveFactors = factors.filter(f => f.impact > 0.5).map(f => f.name);
        const negativeFactors = factors.filter(f => f.impact < -0.5).map(f => f.name);
        
        let reasoning = `Classified as ${level.toUpperCase()}.`;
        
        if (positiveFactors.length > 0) {
            reasoning += ` Complexity increased by: ${positiveFactors.join(', ')}.`;
        }
        
        if (negativeFactors.length > 0) {
            reasoning += ` Simplified by: ${negativeFactors.join(', ')}.`;
        }
        
        return reasoning;
    }
}

// Export singleton instance
export const complexityAnalyzer = new AdvancedComplexityAnalyzer();

// Convenience function
export function analyzeComplexity(
    message: string,
    attachments: Attachment[] = [],
    conversationHistory: number = 0
): ComplexityAnalysis {
    return complexityAnalyzer.analyze(message, attachments, conversationHistory);
}
